import {BaseQueryFn} from '@reduxjs/toolkit/dist/query';
import axios, {AxiosError, AxiosRequestConfig, Method} from 'axios';
import CryptoJs from 'crypto-js';
import {authApi} from 'src/api';
import {store} from 'src/app/store';
import {Times} from 'src/utils/consts';
import {authHeadersFromLocalStorage, cryptoKey, rejectError} from './interceprtors';

const configuration: AxiosRequestConfig = {
    url: '/',
    method: 'get' as Method,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
    timeout: 45000,
    responseType: 'json',
    maxContentLength: 2000,
    validateStatus: (status: number) => status >= 200 && status < 300,
    maxRedirects: 5,
};

export const axiosInstance = axios.create(configuration);

// A cancelled/aborted request is NORMAL (RTK Query aborts in-flight requests on
// navigation / unmount / supersede). It must NEVER be treated as an auth failure.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isAbortError = (e: any): boolean => {
    if (!e) return false;
    if (axios.isCancel(e)) return true;
    // The cancel marker can live in `message` (raw axios error) or in `data`
    // (our RTK-wrapped `{status, data}` shape from axiosBaseQuery).
    const str = typeof e.message === 'string' ? e.message : typeof e.data === 'string' ? e.data : '';
    return (
        e.status === 'ABORTED' ||
        e.code === 'ERR_CANCELED' ||
        e.name === 'CanceledError' ||
        e.name === 'AbortError' ||
        str === 'Request aborted' ||
        str === 'canceled'
    );
};

axiosInstance.interceptors.request.use(authHeadersFromLocalStorage, rejectError);

axiosInstance.interceptors.response.use(async (response) => {
    const {headers} = response;
    const isNewAuthRequest = headers.authorization && headers['refresh-token'];

    if (!isNewAuthRequest) {
        return response;
    }

    const tokenData = JSON.parse(decodeURIComponent(atob(headers.authorization.split('.')[1])));
    const tokenIsExpired = tokenData.exp * Times.ONE_S_IN_MS <= new Date().getTime();

    if (tokenIsExpired) {
        localStorage.clear();
        return response;
    }

    const authData = {
        authorization: headers.authorization,
        refreshToken: headers['refresh-token'],
        expireDate: tokenData.exp,
    };
    const crypto = CryptoJs.AES.encrypt(JSON.stringify(authData), cryptoKey.toString()).toString();
    localStorage.setItem('auth', crypto);
    return response;
}, undefined);

// What the server says when it REFUSES a session, as opposed to failing to judge
// one: 401 from the authentication filter, 403 or 406 from a rejected refresh
// token (which of those two depends on the environment - the server's
// `amp.permissions.deny-status`). Any other answer to the refresh call leaves
// the session alone.
const SESSION_REFUSED: Array<number> = [401, 403, 406];

// Clears the session and hard-redirects to login, so no stale (cached) data
// stays on screen after an unrecoverable auth failure (e.g. token expired AND
// the server was restarted, so the refresh token is no longer accepted).
const forceLogout = () => {
    localStorage.clear();
    if (window.location.pathname !== '/login') {
        window.location.assign('/login');
    }
};

// Single-flight token refresh: when a burst of requests fails with 401 at once
// (e.g. a page re-asking every query it holds), they all await the SAME refresh
// call instead of each firing its own.
let refreshPromise: Promise<void> | null = null;

/**
 * One renewal at a time, and one request per renewal.
 *
 * `refreshToken` is a MUTATION, and the two reasons are the two halves of a bug that logged people
 * out of sessions that were alive. A query's `initiate` SUBSCRIBES, and nothing here ever
 * unsubscribed, so the renewal stayed in the store as a cache entry - and once `refetchOnFocus` was
 * switched on, every return to the tab re-POSTed `/auth/refresh/token` with whatever token had been
 * captured the first time. Worse, a query's thunk refuses to run while a request for the same key is
 * in flight: `condition` checks `pending` BEFORE it looks at `forceRefetch`, so the dispatch became a
 * no-op and `unwrap()` resolved IMMEDIATELY off the cached entry. The caller believed the renewal had
 * happened and retried its request with the access token that had just expired, and an entry left
 * holding an error made `unwrap()` throw instead - which cleared the session.
 *
 * A mutation has no cache key, no subscription and no such condition, so `refreshPromise` above is
 * once again the only thing deciding who waits for whom. `track: false` keeps even the result out of
 * the store: nothing renders it.
 */
const runRefresh = (token: string): Promise<void> => {
    if (!refreshPromise) {
        refreshPromise = store
            .dispatch(authApi.endpoints.refreshToken.initiate({token}, {track: false}))
            .unwrap()
            .then(() => undefined)
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
};

axiosInstance.interceptors.response.use(undefined, async (error: AxiosError<{messages?: string}>) => {
    const response = error.response;
    const config = error.config as (AxiosRequestConfig & {_retry?: boolean}) | undefined;

    // Aborted/cancelled request (navigation, unmount, RTK Query supersede) — not
    // an auth problem. Reject quietly so the outer retry catch never logs out.
    if (isAbortError(error)) {
        return Promise.reject(error);
    }

    // Network / CORS / timeout — nothing to recover.
    if (!response || !config) {
        return Promise.reject(error);
    }

    const url = config.url ?? '';

    // A failed login is surfaced to the login form itself.
    if (url.includes('/auth/login')) {
        return Promise.reject(error);
    }

    // The refresh endpoint has answered for itself. A REFUSAL ends the session - the row is gone,
    // because somebody signed out, or changed the password, or it aged out. Anything else is not a
    // verdict on the session: a 502 from the proxy, a timeout, a connection dropped while the tab was
    // waking up. Those used to take the same branch, which is how a live session was lost to one bad
    // moment on the network.
    if (url.includes('/auth/refresh/token')) {
        if (SESSION_REFUSED.includes(response.status)) {
            forceLogout();
        }
        return Promise.reject(error);
    }

    // Only unauthenticated responses are recoverable via a token refresh.
    if (response.status !== 401) {
        return Promise.reject(error);
    }

    // Read the stored session once.
    const auth = localStorage.getItem('auth');
    if (!auth) {
        forceLogout();
        return Promise.reject(error);
    }

    let localData: {refreshToken?: string; expireDate?: number} | undefined;
    try {
        localData = JSON.parse(
            CryptoJs.AES.decrypt(auth, cryptoKey.toString()).toString(CryptoJs.enc.Utf8)
        );
    } catch {
        forceLogout();
        return Promise.reject(error);
    }

    // A 401 while the access token is STILL VALID is NOT a token problem — e.g. a
    // not-fully-registered user (or an insufficient-permission case) hitting a
    // gated endpoint. Refreshing won't help and a failing refresh would log the
    // user out, so just reject and let the caller handle the 401.
    const expireDate = localData?.expireDate;
    const tokenStillValid = !!expireDate && expireDate * Times.ONE_S_IN_MS > new Date().getTime();
    if (tokenStillValid) {
        return Promise.reject(error);
    }

    // Already refreshed + retried once and STILL 401 → give up, but only log out
    // if the session is actually gone.
    if (config._retry) {
        if (!localStorage.getItem('auth')) {
            forceLogout();
        }
        return Promise.reject(error);
    }
    config._retry = true;

    const refreshTokenValue = localData?.refreshToken;
    if (!refreshTokenValue) {
        forceLogout();
        return Promise.reject(error);
    }

    try {
        await runRefresh(refreshTokenValue);
        // Refresh succeeded — the success interceptor persisted the new token;
        // retry the original request once (the request interceptor re-reads it).
        return await axiosInstance.request(config);
    } catch (refreshError) {
        // The original request, its retry, or the refresh call itself was simply
        // cancelled — e.g. the user navigated away. Do NOT log out; the refresh
        // may well have succeeded and the new token is already stored.
        const aborted =
            isAbortError(refreshError) || (config.signal as AbortSignal | undefined)?.aborted;
        if (aborted) {
            return Promise.reject(refreshError);
        }
        // Nothing is cleared here. The decision was taken above, where the refresh endpoint's own
        // answer was read: a refusal ended the session there, and anything else deliberately left it
        // standing. Deciding it again here was the other half of the bug - every failure to REACH a
        // verdict cost the session.
        // Surface the ORIGINAL 401 (a real AxiosError) instead of the refresh
        // call's RTK-shaped `{status, data}` plain object — the latter carries
        // no `response`/`message`, so callers could not build an error payload
        // from it.
        return Promise.reject(axios.isAxiosError(refreshError) ? refreshError : error);
    }
});

// Every caller reads a failed query as `{status, data: {message}}` (see
// `isErrorObject` in utils/helper + the ~100 `error.data.message` call sites),
// so the payload must ALWAYS be an object carrying a message. A rejection can
// also arrive ALREADY RTK-shaped (`{status, data}` re-thrown from the refresh
// `.unwrap()`): such an object has neither `response` nor `message`, which used
// to yield `data: undefined` and crash the caller with
// "can't access property 'message', error.data is undefined".
const toErrorData = (body: unknown, fallback: string) => {
    if (body && typeof body === 'object' && !Array.isArray(body)) {
        const payload = body as Record<string, unknown>;
        // The backend is not consistent: `message` on most endpoints,
        // `messages` on auth failures, `error` on a few others.
        const message = payload.message ?? payload.messages ?? payload.error ?? fallback;
        return {...payload, message};
    }
    return {message: typeof body === 'string' && body ? body : fallback};
};

type AxiosBaseQueryRequestConfig = Omit<AxiosRequestConfig, 'url' | 'method'> & {
    url: string;
    method: NonNullable<AxiosRequestConfig['method']>;
};
export type AxiosBaseQueryFn = BaseQueryFn<AxiosBaseQueryRequestConfig, unknown, unknown>;

export const axiosBaseQuery =
    ({baseUrl}: { baseUrl: string } = {baseUrl: ''}): AxiosBaseQueryFn =>
        async ({url, ...rest}) => {
            try {
                const result = await axiosInstance({url: baseUrl + url, ...rest});
                return {data: result.data};
            } catch (axiosError) {
                const err = axiosError as AxiosError & {status?: number | string; data?: unknown};
                // Cancelled/aborted request (navigation, unmount, RTK Query
                // supersede) — mark it distinctly so the refresh flow never
                // mistakes it for an auth failure and logs the user out. The
                // payload stays a plain marker string on purpose: `isErrorObject`
                // then rejects it and no error toast is shown for a cancellation.
                if (isAbortError(err)) {
                    return {error: {status: 'ABORTED', data: 'aborted'}};
                }
                // `err.status` / `err.data` cover an already RTK-shaped rejection.
                const body = err.response?.data ?? err.data;
                return {
                    error: {
                        status: err.response?.status ?? err.status,
                        data: toErrorData(body, err.message || 'Request failed'),
                    },
                };
            }
        };

/**
 * When a query is asked again rather than answered from cache.
 *
 * RTK Query keeps an answer for a minute after its last subscriber goes away and, left to its
 * defaults, hands that answer straight back to the next one without checking. So walking to another
 * page and back showed the numbers and the rows from before, and only a browser reload - which empties
 * the store - ever looked current. Every list in the app behaved that way; the chargers strip was
 * simply where it was noticed. The lists themselves are lazy queries that re-ask on mount, so what
 * stood still was everything built on a plain query: the stats strips above the tables, the detail
 * pages, the dropdowns.
 *
 * One event re-asks:
 *
 * - `refetchOnMountOrArgChange` reacts to React - a page is opened, returned to, or its filter
 *   changes. `true` means "whenever something subscribes", not "always": two components subscribing
 *   in the same pass still share one request, and nothing polls. A number of seconds here instead
 *   means "reuse an answer younger than this", and that is the cheaper setting if this ever needs one.
 *
 * `refetchOnFocus` and `refetchOnReconnect` were here too and are off deliberately. Focus was the
 * expensive one by a distance: returning to a tab happens far more often than opening a page, and it
 * re-asked EVERYTHING mounted in one burst - strips, dropdowns, detail pages. After a long absence
 * every request in that burst carried an expired access token, so the burst reached the server as a
 * storm of 401s, and that storm is what exposed the renewal bug described above `runRefresh`. The
 * renewal is fixed, so this is a judgement about cost and noise rather than a workaround. What it
 * gives up is the page left open since the morning, which goes on showing the morning until something
 * remounts; a screen that genuinely needs more can opt in where it is called, with
 * `{refetchOnFocus: true}`, and the window listeners it needs are still attached in
 * `src/app/store.ts`.
 *
 * One policy, spread into every `createApi`, so changing it is one line here rather than the same
 * paragraph twenty-five times.
 */
export const REFETCH_POLICY = {
    refetchOnMountOrArgChange: true,
} as const;
